import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Typography, Alert, Checkbox } from "antd";
import { SafetyOutlined, MailOutlined, WarningOutlined } from "@ant-design/icons";
import { authService } from "../../services/authService";
import {
    getOtpTrackingId, getOtpMaskedEmail, clearOtpSession,
    getOtpExpireDansSecondes, getOtpRenvoiDansSecondes, getOtpSecondesEcoulees,
    getOtpAuthenticatorAssocie, getOtpValidationAdminPossible,
    getOtpAuthenticatorDisponible,
    getOtpConfiancePossible,
} from "../../variables/constants";
import { consommerDestination } from "../../utils/retourApresConnexion";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";

const { Text } = Typography;

const COLORS = {
    greenDeep: "#0f4525",
    greenMain: "#1a6b3c",
    greenMid: "#23894e",
    greenLight: "#2db366",
    white: "#ffffff",
    offWhite: "#f4faf7",
    greyBorder: "#d0e8d9",
    greyText: "#5a7a66",
};

/* ── Constantes ─────────────────────────────────────────────────────────────── */
const TOTAL = 6;
// Valeurs de repli : les vraies durées viennent du serveur (§ setOtpSession), qui
// seul connaît le paramétrage appliqué.
const TIMER_START = 300;
const RESEND_COOLDOWN = 60;
// Le recours « je ne reçois pas le code » n'apparaît qu'après ce délai. Le
// proposer d'emblée en ferait le chemin le plus court, et la file des
// administrateurs se remplirait de demandes que le courriel aurait réglées.
const DELAI_RECOURS_SECONDES = 90;

/* ── Helpers ────────────────────────────────────────────────────────────────── */
const formatTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
};

/* Les trois decomptes se LISENT, ils ne se decrementent pas.
 *
 * Ils partaient chacun d'une constante et perdaient une seconde par tick. Deux
 * consequences : un rechargement les faisait repartir du debut - le motif de ce
 * correctif - et un onglet mis en arriere-plan, dont le navigateur bride les
 * minuteries, prenait du retard sans jamais le rattraper. Les deux disparaissent
 * si l'affichage se deduit d'une echeance absolue a chaque battement, au lieu de
 * s'accumuler.
 *
 * Le repli, lui, n'est pas persiste : il ne sert que si le serveur n'a annonce
 * aucune duree, et ce cas degrade garde l'ancien comportement. */
const resteOuRepli = (lire, repli, ancre) => {
    const reste = lire();
    if (reste !== null) return reste;
    if (ancre.current === null) ancre.current = Date.now() + repli * 1000;
    return Math.max(0, Math.ceil((ancre.current - Date.now()) / 1000));
};

export default function OTPPage() {
    const { t } = useTranslation("security");
    const navigate = useNavigate();
    const ancreExpiration = useRef(null);
    const ancreRenvoi = useRef(null);
    const [values, setValues] = useState(Array(TOTAL).fill(""));
    const [focused, setFocused] = useState(0);
    const [hasError, setHasError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [seconds, setSeconds] = useState(() => getOtpExpireDansSecondes() ?? TIMER_START);
    const [expired, setExpired] = useState(false);
    const [maskedEmail, setMaskedEmail] = useState("");
    const [resendCooldown, setResendCooldown] = useState(() => getOtpRenvoiDansSecondes() ?? RESEND_COOLDOWN);
    const [resendLoading, setResendLoading] = useState(false);
    const [faireConfiance, setFaireConfiance] = useState(false);
    const [attente, setAttente] = useState(getOtpSecondesEcoulees);
    const [recoursPossible, setRecoursPossible] = useState(true);
    const [confiancePossible, setConfiancePossible] = useState(false);
    const [recoursEnvoye, setRecoursEnvoye] = useState(false);
    const [recoursLoading, setRecoursLoading] = useState(false);

    /* Voie de secours : l'utilisateur saisit le code de son application au lieu
       d'attendre un courriel qui n'arrive pas. */
    const [authenticatorDisponible, setAuthenticatorDisponible] = useState(false);
    /* Deja associe, ou pas encore : l'ecran n'a pas la meme chose a proposer. Saisir
       un code suppose une application ; sans elle, la seule issue est l'accord d'un
       administrateur, apres quoi l'association se fait depuis « Mon profil ». */
    const [authenticatorAssocie, setAuthenticatorAssocie] = useState(false);
    const [modeAuthenticator, setModeAuthenticator] = useState(false);
    /* 'VERIFICATION' puis, le cas échéant, 'ENROLEMENT' - proposé seulement APRÈS
       un code e-mail validé, jamais avant : le second facteur doit être prouvé. */
    const [etape, setEtape] = useState("VERIFICATION");
    const [enrolement, setEnrolement] = useState(null);
    const inputRefs = useRef([]);

    /* ── Garde session OTP ── */
    useEffect(() => {
        if (!getOtpTrackingId()) { navigate("/login", { replace: true }); return; }
        setMaskedEmail(getOtpMaskedEmail() || "");
        setSeconds(getOtpExpireDansSecondes() ?? TIMER_START);
        setAuthenticatorDisponible(getOtpAuthenticatorDisponible());
        setAuthenticatorAssocie(getOtpAuthenticatorAssocie());
        setResendCooldown(getOtpRenvoiDansSecondes() ?? RESEND_COOLDOWN);
        setRecoursPossible(getOtpValidationAdminPossible());
        setConfiancePossible(getOtpConfiancePossible());
        focusAt(0);
    }, [navigate]);

    /* ── Timer principal ── */
    useEffect(() => {
        if (expired || success) return;
        const battre = () => {
            const reste = resteOuRepli(getOtpExpireDansSecondes, TIMER_START, ancreExpiration);
            setSeconds(reste);
            if (reste <= 0) setExpired(true);
        };
        battre();
        const id = setInterval(battre, 1000);
        return () => clearInterval(id);
    }, [expired, success]);

    /* ── Temps passé sur l'écran : conditionne l'affichage du recours ── */
    useEffect(() => {
        if (success || recoursEnvoye) return;
        const id = setInterval(() => setAttente(getOtpSecondesEcoulees()), 1000);
        return () => clearInterval(id);
    }, [success, recoursEnvoye]);

    /* ── Cooldown renvoi ──
       Un seul intervalle, arrete quand le decompte atteint zero. L'ancienne
       version dependait de `resendCooldown` : elle detruisait et recreait son
       intervalle a chaque seconde ecoulee, et le decompte derivait d'autant. */
    useEffect(() => {
        if (resendCooldown <= 0) return undefined;
        const id = setInterval(() => {
            setResendCooldown(resteOuRepli(getOtpRenvoiDansSecondes, RESEND_COOLDOWN, ancreRenvoi));
        }, 1000);
        return () => clearInterval(id);
    }, [resendCooldown]);

    /* ── Focus helper ── */
    const focusAt = (i) => {
        const el = inputRefs.current[i];
        if (el) { el.focus(); setFocused(i); }
    };

    /* ── Handlers clavier / paste ── */
    const handleChange = (i, e) => {
        const raw = e.target.value.replace(/\D/g, "");
        if (!raw) return;
        if (raw.length > 1) {
            const digits = raw.slice(0, TOTAL).split("");
            const next = [...values];
            digits.forEach((d, j) => { if (next[j] !== undefined) next[j] = d; });
            setValues(next);
            setHasError(false);
            focusAt(Math.min(digits.length, TOTAL - 1));
            return;
        }
        const next = [...values];
        next[i] = raw[0];
        setValues(next);
        setHasError(false);
        setErrorMsg("");
        if (i < TOTAL - 1) focusAt(i + 1);
    };

    const handleKeyDown = (i, e) => {
        /* « Entrée » valide, comme le bouton. Le code saisi, il fallait lâcher le
           clavier pour aller chercher la souris - sur l'écran qu'on traverse à
           chaque connexion, et au moment précis où le code court contre son
           délai d'expiration. */
        if (e.key === "Enter") {
            e.preventDefault();
            soumettre();
            return;
        }
        if (e.key === "Backspace") {
            e.preventDefault();
            if (values[i]) {
                const next = [...values]; next[i] = ""; setValues(next);
            } else if (i > 0) {
                const next = [...values]; next[i - 1] = ""; setValues(next);
                focusAt(i - 1);
            }
        }
        if (e.key === "ArrowLeft" && i > 0) focusAt(i - 1);
        if (e.key === "ArrowRight" && i < TOTAL - 1) focusAt(i + 1);
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, TOTAL);
        const next = Array(TOTAL).fill("");
        text.split("").forEach((d, j) => { next[j] = d; });
        setValues(next);
        focusAt(Math.min(text.length, TOTAL - 1));
    };

    /* ── Vérification ── */
    const handleVerify = async () => {
        const code = values.join("");
        if (code.length < TOTAL || loading || success) return;
        setLoading(true);
        setHasError(false);
        setErrorMsg("");
        try {
            const data = await authService.verifyOtp(code, faireConfiance,
                modeAuthenticator ? "AUTHENTICATOR" : "EMAIL");
            setSuccess(true);
            toast.success(t("otp.messages.authentificationReussie"));

            // Le second facteur vient d'être prouvé : seul moment où associer une
            // application d'authentification sans affaiblir le 2FA.
            if (data?.enrolementPropose) {
                setEnrolement(data.enrolementPropose);
                setEtape("ENROLEMENT");
                setValues(Array(TOTAL).fill(""));
                setSuccess(false);
                setLoading(false);
                setTimeout(() => focusAt(0), 50);
                return;
            }
            // Même retour qu'après une connexion sans second facteur : la destination
            // mémorisée doit survivre à l'étape OTP, sinon un lien de courriel ouvert
            // par un agent en double authentification retombe sur le tableau de bord.
            setTimeout(() => navigate(consommerDestination(), { replace: true }), 800);
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            const msg = data?.message || err?.message || t("otp.messages.erreurVerification");
            setHasError(true);
            setValues(Array(TOTAL).fill(""));
            focusAt(0);
            if (status === 410) {
                setExpired(true);
                setErrorMsg(t("otp.messages.codeExpire"));
            } else if (status === 423) {
                const errMsg = t("otp.messages.tentativesEpuisees");
                setErrorMsg(errMsg);
                clearOtpSession();
                setTimeout(() => navigate("/login", { replace: true }), 2000);
            } else if (status === 400) {
                const remaining = data?.data?.attemptsRemaining;
                setErrorMsg(remaining !== undefined
                    ? t("otp.messages.codeIncorrectRestantes", { restantes: remaining })
                    : msg);
            } else {
                setErrorMsg(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    /* ── Renvoi ── */
    const handleResend = async () => {
        if (resendCooldown > 0 || resendLoading) return;
        setResendLoading(true);
        try {
            await authService.resendOtp();
            setValues(Array(TOTAL).fill(""));
            setExpired(false);
            // authService.resendOtp a deja rafraichi la session OTP : on repart des
            // durees que le serveur vient de renvoyer, pas des replis codes en dur.
            ancreExpiration.current = null;
            ancreRenvoi.current = null;
            setSeconds(getOtpExpireDansSecondes() ?? TIMER_START);
            setHasError(false);
            setErrorMsg("");
            setSuccess(false);
            setResendCooldown(getOtpRenvoiDansSecondes() ?? RESEND_COOLDOWN);
            focusAt(0);
            toast.success(t("otp.messages.nouveauCodeEnvoye"));
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            if (status === 429) {
                const secs = data?.data?.secondsRemaining ?? RESEND_COOLDOWN;
                setResendCooldown(Number(secs));
                toast.error(t("otp.messages.patienter", { secondes: secs }));
            } else {
                toast.error(data?.message || err?.message || t("otp.messages.erreurRenvoi"));
            }
        } finally {
            setResendLoading(false);
        }
    };

    /* ── « Je ne reçois pas le code » ── */
    const handleRecours = async () => {
        if (recoursLoading || recoursEnvoye) return;
        setRecoursLoading(true);
        try {
            await authService.demanderValidationAppareil({ motif: "OTP_NON_RECU" });
            setRecoursEnvoye(true);
            toast.success(t("otp.messages.demandeTransmise"));
            setTimeout(() => navigate("/login", { replace: true }), 4000);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message
                || t("otp.messages.demandeImpossible"));
        } finally {
            setRecoursLoading(false);
        }
    };

    const basculerMode = (actif) => {
        setModeAuthenticator(actif);
        setValues(Array(TOTAL).fill(""));
        setHasError(false);
        setErrorMsg("");
        setTimeout(() => focusAt(0), 50);
    };

    const handleConfirmerEnrolement = async () => {
        const saisi = values.join("");
        if (saisi.length < TOTAL || loading) return;
        setLoading(true);
        setHasError(false);
        setErrorMsg("");
        try {
            await authService.confirmerAuthenticator(saisi);
            toast.success(t("otp.messages.applicationAssociee"));
            navigate(consommerDestination(), { replace: true });
        } catch (err) {
            setHasError(true);
            setValues(Array(TOTAL).fill(""));
            focusAt(0);
            setErrorMsg(err?.response?.data?.message
                || t("otp.messages.codeIncorrectHeure"));
        } finally {
            setLoading(false);
        }
    };

    const passerEnrolement = () => navigate(consommerDestination(), { replace: true });

    const code = values.join("");
    const enrolementEnCours = etape === "ENROLEMENT";
    const isDisabled = code.length < TOTAL || loading || success
        || (!modeAuthenticator && !enrolementEnCours && expired)
        // Case cochee sans application associee : aucun code ne peut etre valide.
        // Laisser le bouton actif ne menerait qu'au refus du serveur.
        || (modeAuthenticator && !authenticatorAssocie);

    /* UNE SEULE ACTION pour le bouton et pour la touche Entrée. Écrites à deux
       endroits, les deux voies auraient fini par diverger : Entrée aurait validé
       un code expiré, ou vérifié pendant l'association d'une application au lieu
       de la confirmer. La garde sur `isDisabled` est ici essentielle - le bouton
       désactivé ne répond pas au clic, mais rien n'empêche d'appuyer sur Entrée. */
    const soumettre = () => {
        if (isDisabled) return;
        if (enrolementEnCours) handleConfirmerEnrolement();
        else handleVerify();
    };
    /* Le recours s'ouvre après un délai d'attente, ou tout de suite si le code a
       expiré sans jamais arriver - et sans attendre pour qui vient de découvrir
       qu'aucune application n'est associée à son compte : c'est desormais sa seule
       voie, la lui faire attendre quatre-vingt-dix secondes n'a plus de sens. */
    const afficherRecours = recoursPossible && !success
        && (attente >= DELAI_RECOURS_SECONDES || expired
            || (modeAuthenticator && !authenticatorAssocie));

    return (
        <>
            <style>{`
                @keyframes blobFloat {
                    from { transform: translate(0,0) scale(1); }
                    to   { transform: translate(24px,18px) scale(1.07); }
                }
                @keyframes cardIn {
                    from { opacity:0; transform:translateY(28px) scale(.97); }
                    to   { opacity:1; transform:none; }
                }
                @keyframes shake {
                    0%,100% { transform:translateX(0); }
                    20% { transform:translateX(-7px); }
                    40% { transform:translateX(7px); }
                    60% { transform:translateX(-4px); }
                    80% { transform:translateX(4px); }
                }
                .otp-input {
                    width: 58px; height: 66px;
                    border: 2.5px solid ${COLORS.greyBorder};
                    border-radius: 14px;
                    background: ${COLORS.offWhite};
                    font-size: 1.65rem; font-weight: 800;
                    color: ${COLORS.greenDeep};
                    text-align: center; outline: none;
                    transition: border-color .2s, box-shadow .2s, transform .15s, background .2s;
                    caret-color: transparent;
                }
                .otp-input:focus {
                    border-color: ${COLORS.greenMain};
                    box-shadow: 0 0 0 4px ${COLORS.greenMain}22;
                    transform: translateY(-2px);
                    background: ${COLORS.white};
                }
                .otp-input.filled {
                    border-color: ${COLORS.greenMid};
                    background: ${COLORS.white};
                }
                .otp-input.error {
                    border-color: #e74c3c !important;
                    animation: shake .4s ease;
                }
                input[type=tel]::-webkit-inner-spin-button,
                input[type=tel]::-webkit-outer-spin-button { -webkit-appearance: none; }
            `}</style>

            {/* ── Page background ── */}
            <div style={{
                minHeight: "calc(100vh - var(--cb-banniere-h, 0px))",
                background: `linear-gradient(145deg, ${COLORS.greenDeep} 0%, #163d28 60%, #0a2e18 100%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                position: "relative", overflow: "hidden", fontFamily: "system-ui, sans-serif",
            }}>
                {/* Blobs déco */}
                {[
                    { top: "-10%", left: "-8%", size: 480, delay: "0s" },
                    { top: "60%", left: "70%", size: 380, delay: "-5s" },
                    { top: "30%", left: "50%", size: 280, delay: "-2.5s" },
                ].map((b, i) => (
                    <div key={i} style={{
                        position: "absolute", width: b.size, height: b.size, borderRadius: "50%",
                        background: `radial-gradient(circle, ${COLORS.greenMid}55, transparent 70%)`,
                        top: b.top, left: b.left, filter: "blur(70px)", pointerEvents: "none",
                        animation: `blobFloat 10s ease-in-out ${b.delay} infinite alternate`,
                    }} />
                ))}
                {/* Grille */}
                <div style={{
                    position: "absolute", inset: 0, pointerEvents: "none",
                    backgroundImage: `linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),
                                      linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px)`,
                    backgroundSize: "36px 36px",
                }} />

                {/* ── Carte principale ── */}
                <div style={{
                    position: "relative", zIndex: 1,
                    background: COLORS.white, borderRadius: 28,
                    padding: "52px 48px 44px",
                    width: "100%", maxWidth: 460, margin: "16px",
                    boxShadow: `0 32px 80px rgba(15,69,37,.28), 0 4px 20px rgba(15,69,37,.15)`,
                    animation: "cardIn .6s cubic-bezier(.22,1,.36,1) both",
                }}>

                    {/* Logo */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 32 }}>
                        <div style={{
                            width: 50, height: 50, borderRadius: 14, flexShrink: 0,
                            background: `linear-gradient(135deg, ${COLORS.greenMain}, ${COLORS.greenLight})`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: `0 6px 20px ${COLORS.greenMain}44`,
                        }}>
                            <SafetyOutlined style={{ fontSize: 24, color: "#fff" }} />
                        </div>
                        <div style={{ fontSize: "1.8rem", fontWeight: 800, letterSpacing: "-0.5px", color: COLORS.greenMain, lineHeight: 1 }}>
                            CB<span style={{ color: COLORS.greenLight }}>Connect</span>
                        </div>
                    </div>

                    {/* Titre */}
                    <div style={{ textAlign: "center", fontSize: "1.35rem", fontWeight: 700, color: COLORS.greenDeep, marginBottom: 8 }}>
                        {t("otp.titre")}
                    </div>
                    <div style={{ textAlign: "center", fontSize: "0.875rem", color: COLORS.greyText, lineHeight: 1.6, marginBottom: 6 }}>
                        <MailOutlined style={{ marginRight: 6, color: COLORS.greenMain }} />
                        <Trans t={t} i18nKey="otp.codeEnvoyeA" values={{ email: maskedEmail || t("otp.votreEmail") }}
                            components={{ 1: <Text strong style={{ color: COLORS.greenMain }} /> }} />
                    </div>
                    <div style={{
                        width: 44, height: 3, borderRadius: 2,
                        background: `linear-gradient(90deg, ${COLORS.greenMain}, ${COLORS.greenLight})`,
                        margin: "18px auto 30px",
                    }} />

                    {/* Alerte erreur */}
                    {hasError && errorMsg && (
                        <Alert
                            type="error"
                            message={errorMsg}
                            showIcon
                            closable
                            onClose={() => { setHasError(false); setErrorMsg(""); }}
                            style={{ marginBottom: 18, borderRadius: 10 }}
                        />
                    )}

                    {/* Champs OTP */}
                    <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 8 }}>
                        {values.map((val, i) => (
                            <input
                                key={i}
                                ref={(el) => (inputRefs.current[i] = el)}
                                type="tel"
                                inputMode="numeric"
                                maxLength={6}
                                value={val}
                                className={[
                                    "otp-input",
                                    val ? "filled" : "",
                                    hasError ? "error" : "",
                                ].filter(Boolean).join(" ")}
                                onFocus={() => setFocused(i)}
                                onBlur={() => setFocused(-1)}
                                onChange={(e) => handleChange(i, e)}
                                onKeyDown={(e) => handleKeyDown(i, e)}
                                onPaste={handlePaste}
                                aria-label={t("otp.chiffre", { numero: i + 1 })}
                                disabled={success}
                            />
                        ))}
                    </div>

                    {/* QR d'association */}
                    {enrolementEnCours && enrolement && (
                        <div style={{ textAlign: "center", margin: "4px 0 20px" }}>
                            <img
                                src={`data:image/png;base64,${enrolement.qrCodeBase64}`}
                                alt={t("otp.enrolement.qrAlt")}
                                style={{
                                    width: 168, height: 168, borderRadius: 12,
                                    border: `1.5px solid ${COLORS.greyBorder}`, padding: 8,
                                    background: COLORS.white,
                                }}
                            />
                            <div style={{ marginTop: 12, fontSize: "0.78rem", color: COLORS.greyText }}>
                                {t("otp.enrolement.impossibleScanner")}
                            </div>
                            <div style={{
                                marginTop: 6, fontFamily: "ui-monospace, monospace",
                                fontSize: "0.82rem", fontWeight: 700, letterSpacing: "1px",
                                color: COLORS.greenDeep, background: COLORS.offWhite,
                                border: `1px solid ${COLORS.greyBorder}`, borderRadius: 8,
                                padding: "8px 12px", wordBreak: "break-all",
                            }}>
                                {enrolement.secretBase32}
                            </div>
                        </div>
                    )}

                    {/* Voie de secours : proposée seulement si l'organisme l'autorise
                        ET que ce compte a déjà associé une application. */}
                    {!enrolementEnCours && authenticatorDisponible && (
                        <div style={{ marginTop: 16, textAlign: "center" }}>
                            <Checkbox
                                checked={modeAuthenticator}
                                disabled={success || loading}
                                onChange={(e) => basculerMode(e.target.checked)}
                                style={{ fontSize: "0.845rem", color: COLORS.greyText }}
                            >
                                {t("otp.authenticator.utiliser")}
                            </Checkbox>

                            {/* Cochee sans application associee, la case menerait a un champ
                                que rien ne peut remplir. On dit pourquoi, et on renvoie vers
                                la seule voie ouverte : l'accord d'un administrateur. Le QR,
                                lui, ne s'affiche jamais ici - le mot de passe est la seule
                                chose prouvee a cet instant, et scanner un code a cet endroit
                                laisserait un mot de passe vole suffire a entrer. */}
                            {modeAuthenticator && !authenticatorAssocie && (
                                <div style={{
                                    marginTop: 12, padding: "12px 14px", textAlign: "left",
                                    background: COLORS.offWhite, border: `1px solid ${COLORS.greyBorder}`,
                                    borderRadius: 10, fontSize: "0.8rem", color: COLORS.greyText,
                                    lineHeight: 1.55,
                                }}>
                                    <strong style={{ color: COLORS.greenDeep }}>
                                        {t("otp.authenticator.aucuneAssociee")}
                                    </strong>
                                    {/* Les comptes a privileges sont exclus de la validation par
                                        administrateur, et nul n'approuve sa propre demande : leur
                                        promettre un recours « ci-dessous » designait un bouton qui
                                        ne s'affiche jamais pour eux. */}
                                    <div style={{ marginTop: 4 }}>
                                        {recoursPossible ? (
                                            <Trans t={t} i18nKey="otp.authenticator.avecRecours" components={{ 1: <em /> }} />
                                        ) : (
                                            <Trans t={t} i18nKey="otp.authenticator.sansRecours" components={{ 1: <em /> }} />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Timer - sans objet pour un code que le serveur ne fait pas périmer */}
                    {!modeAuthenticator && !enrolementEnCours && (
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            gap: 8, margin: "18px 0 22px", fontSize: "0.83rem", color: COLORS.greyText,
                        }}>
                            <span>{t("otp.codeValidePendant")}</span>
                            <span style={{
                                background: expired ? "#fff5f5" : COLORS.offWhite,
                                border: `1.5px solid ${expired ? "#f5c6c6" : COLORS.greyBorder}`,
                                borderRadius: 20, padding: "3px 14px",
                                fontWeight: 700,
                                color: expired ? "#c0392b" : COLORS.greenMain,
                                minWidth: 56, textAlign: "center",
                                fontVariantNumeric: "tabular-nums", fontSize: "0.83rem",
                            }}>
                                {expired ? t("otp.expire") : formatTime(seconds)}
                            </span>
                        </div>
                    )}

                    {(modeAuthenticator || enrolementEnCours) && <div style={{ height: 20 }} />}

                    {/* Faire confiance à cet appareil - jamais cochée par défaut, et
                        masquée quand le serveur dit qu'elle n'aurait aucun effet
                        (mode observation, ou organisme sans appareil de confiance). */}
                    {confiancePossible && <div style={{
                        background: COLORS.offWhite,
                        border: `1.5px solid ${COLORS.greyBorder}`,
                        borderRadius: 14, padding: "14px 16px", marginBottom: 18,
                    }}>
                        <Checkbox
                            checked={faireConfiance}
                            disabled={success}
                            onChange={(e) => setFaireConfiance(e.target.checked)}
                        >
                            <span style={{ fontWeight: 600, color: COLORS.greenDeep, fontSize: "0.9rem" }}>
                                {t("otp.confiance.titre")}
                            </span>
                        </Checkbox>
                        <div style={{
                            marginTop: 6, marginLeft: 24, fontSize: "0.78rem",
                            color: COLORS.greyText, lineHeight: 1.5,
                        }}>
                            {t("otp.confiance.texte")}
                        </div>
                    </div>}

                    {/* Avertissement - un code de connexion ne se transmet jamais */}
                    <div style={{
                        display: "flex", gap: 10, alignItems: "flex-start",
                        background: "#fff6f6", border: "1px solid #f5c6c6",
                        borderRadius: 12, padding: "12px 14px", marginBottom: 20,
                        fontSize: "0.79rem", color: "#8c2f28", lineHeight: 1.55,
                    }}>
                        <WarningOutlined style={{ marginTop: 2, flexShrink: 0 }} />
                        <span>
                            {t("otp.avertissement")}
                        </span>
                    </div>

                    {/* Bouton vérifier */}
                    <Button
                        type="primary"
                        block
                        size="large"
                        loading={loading}
                        disabled={isDisabled}
                        onClick={soumettre}
                        style={{
                            height: 50, borderRadius: 14, fontWeight: 700, fontSize: "0.97rem",
                            background: success
                                ? "linear-gradient(135deg,#16a34a,#22c55e)"
                                : `linear-gradient(135deg, ${COLORS.greenMain} 0%, ${COLORS.greenLight} 100%)`,
                            border: "none",
                            boxShadow: `0 8px 24px ${COLORS.greenMain}44`,
                        }}
                    >
                        {success
                            ? t("otp.verifieAvecSucces")
                            : enrolementEnCours
                                ? t("otp.enrolement.confirmer")
                                : t("otp.verifierCode")}
                    </Button>

                    {/* Association jamais bloquante : on doit pouvoir la remettre à plus tard */}
                    {enrolementEnCours && (
                        <div style={{ textAlign: "center", marginTop: 18 }}>
                            <Button type="link" size="small" onClick={passerEnrolement}
                                style={{ fontWeight: 600, fontSize: "0.855rem", color: COLORS.greyText }}>
                                {t("otp.enrolement.plusTard")}
                            </Button>
                        </div>
                    )}

                    {/* Renvoi */}
                    {!modeAuthenticator && !enrolementEnCours && (
                        <div style={{ textAlign: "center", marginTop: 20, fontSize: "0.855rem", color: COLORS.greyText }}>
                            {t("otp.pasRecu")}{" "}
                            <Button
                                type="link"
                                size="small"
                                loading={resendLoading}
                                disabled={resendCooldown > 0 || success || resendLoading}
                                onClick={handleResend}
                                style={{
                                    padding: "0 4px",
                                    fontWeight: 600,
                                    fontSize: "0.855rem",
                                    color: resendCooldown > 0 || success ? COLORS.greyText : COLORS.greenMain,
                                }}
                            >
                                {resendCooldown > 0 ? t("otp.renvoyerDans", { secondes: resendCooldown }) : t("otp.renvoyer")}
                            </Button>
                        </div>
                    )}

                    {/* Recours : demander la validation par un administrateur */}
                    {recoursEnvoye ? (
                        <Alert
                            type="success"
                            showIcon
                            style={{ marginTop: 16, borderRadius: 12 }}
                            message={t("otp.recours.transmise")}
                            description={t("otp.recours.transmiseTexte")}
                        />
                    ) : afficherRecours && (
                        <div style={{ textAlign: "center", marginTop: 6 }}>
                            <Button
                                type="link"
                                size="small"
                                loading={recoursLoading}
                                onClick={handleRecours}
                                style={{ fontSize: "0.82rem", color: COLORS.greyText, textDecoration: "underline" }}
                            >
                                {t("otp.recours.jeNeRecoisPas")}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}